import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import { useGiftInvoice, type GiftOfferOut } from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";

/**
 * GiftOfferModal (owner 2026-08-11) — попап подарунка маркетинг-машини.
 *
 * Власник просив «красивий попап з анімацією, де видно, яка вигідна
 * пропозиція». Анімуємо саме те, що людина отримує: дні «набігають» від 30 до
 * 30+бонус (або ціна падає з 990 до 449). Плюс таймер, скільки лишилось від
 * 72 годин — строк реальний, приходить із сервера.
 *
 * ⚠️ Промокодів немає: подарунок привʼязаний до людини на сервері. Тому попап
 * нічого не «видає» — він лише пояснює і веде до тарифів.
 */
function useCountdown(iso?: string): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!iso) return "";
    const left = new Date(iso).getTime() - now;
    if (left <= 0) return "час вийшов";
    const h = Math.floor(left / 3_600_000);
    const m = Math.floor((left % 3_600_000) / 60_000);
    // Owner 2026-08-11: поки лишається більше 72 годин — рахуємо днями
    // (30, 29, 28…), від трьох діб і менше — годинами (72, 71…).
    if (h >= 72) return `${Math.ceil(h / 24)} дн.`;
    return h >= 1 ? `${h} год ${m} хв` : `${m} хв`;
  }, [iso, now]);
}

/** Число, що «набігає» до цільового — щоб подарунок було видно, а не прочитано. */
function useCountUp(target: number, ms = 2200): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target <= 0) return;
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      // easeOutQuart — старт бадьорий, а кінець довгий: власник 2026-08-11
      // сказав, що набігання подарунка «занадто швидке» і його не встигаєш
      // помітити. Тепер ~2.2 с із помітним доїздом останніх днів.
      const eased = 1 - Math.pow(1 - p, 4);
      setV(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

/** Ціна, що «стікає» вниз: 990 → 449. Той самий рух, що й у спецпропозиції. */
function useCountDown(
  from: number,
  to: number,
  durationMs: number,
  enabled: boolean,
): number {
  const [val, setVal] = useState(enabled ? from : to);
  useEffect(() => {
    if (!enabled) {
      setVal(to);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, durationMs, enabled]);
  return val;
}

export function GiftOfferModal({
  gift,
  onClose,
  onGoToTariffs,
}: {
  gift: GiftOfferOut;
  onClose: () => void;
  /** Отримує ключ офера — щоб на сторінці тарифів працював саме він. */
  onGoToTariffs: (offer?: string) => void;
}) {
  const left = useCountdown(gift.expires_at);
  const bonus = gift.bonus_days ?? 0;
  const bonusShown = useCountUp(bonus);
  // Три різні візуали (owner 2026-08-11: за відгук людині показало «449 ₴
  // замість 990», хоча подарунок був «14 днів за 1 ₴»):
  //   days — оплачуєш від 30 днів і отримуєш +N;
  //   discount — ціна стікає 990 → 449;
  //   trial — вхід за 1 ₴ на N днів.
  const kind = gift.kind ?? (bonus > 0 ? "days" : "discount");
  const isDiscount = kind === "discount";
  const isTrial = kind === "trial";
  // Цифри дає СЕРВЕР — із тарифу самого офера. Раніше 990 → 449 і −55% були
  // зашиті тут, тому «два місяці за ціною одного» показувались чужою ціною
  // (owner 2026-09-04). Константи лишились запасним варіантом для старих
  // оферів без тарифу.
  const priceTo = gift.price_uah ?? 449;
  const priceFrom = gift.old_price_uah ?? 990;
  const discount = gift.discount_percent ?? 55;
  const priceShown = useCountDown(priceFrom, priceTo, 3400, kind === "discount");
  const invoiceMut = useGiftInvoice();
  // Оффер з окремою ціною веде ОДРАЗУ в оплату: у списку тарифів такої ціни
  // немає, тож відправляти людину «обрати тариф» було б обманом.
  const hasOwnPrice = Boolean(gift.tariff_slug);

  const handleClick = async () => {
    if (!hasOwnPrice) {
      onGoToTariffs(gift.offer);
      return;
    }
    try {
      const res = await invoiceMut.mutateAsync(gift.offer);
      localStorage.setItem(
        "pending_invoice",
        JSON.stringify({
          order_reference: res.order_reference,
          tariff_slug: gift.tariff_slug,
          started_at_before: Date.now(),
        }),
      );
      const webapp = tg();
      if (webapp?.openLink) webapp.openLink(res.purchase_url);
      else window.open(res.purchase_url, "_blank", "noopener,noreferrer");
      onClose();
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(`Не вдалося створити рахунок: ${(e as Error).message}`);
    }
  };
  // У попапі емодзі вже стоїть окремо (анімоване) — прибираємо його з
  // заголовка, інакше подарунок двоїться.
  const title = (gift.title ?? "").replace(/[\s\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\uFE0F]+$/u, "");

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />

        <div className="text-center mb-4">
          <div className="mb-2 flex justify-center">
            {/* Owner 2026-08-11: вогник у нашому наборі НЕ анімований —
                скрізь ставимо діамант, як у спецпропозиції. */}
            <TgEmoji char="💎" size={48} />
          </div>
          <div className="font-display text-[1.1875rem] font-bold text-ink leading-tight mb-1.5 [text-wrap:balance]">
            {title}
          </div>
          <div className="text-[0.8125rem] text-ink-dim leading-relaxed [text-wrap:balance]">
            {gift.body}
          </div>
        </div>

        {/* Головний візуал: що саме додається */}
        <div className="rounded-2xl bg-primary/[0.06] ring-1 ring-primary/20 p-4 mb-3">
          {isTrial ? (
            /* Owner 2026-08-11: ціна лишається, але оформлена спокійно —
               великі дні, під ними тонкий рядок «усього за 1 ₴». Помаранчева
               пігулка з гривнею поруч із цифрою виглядала важко. */
            <div className="text-center">
              <div
                className="font-display text-[2.125rem] font-bold leading-none text-primary tabular-nums"
                style={{ letterSpacing: "-0.02em" }}
              >
                14 днів
              </div>
              <div className="mt-1.5 font-mono text-[0.75rem] text-ink-dim">
                усього за 1 ₴
              </div>
            </div>
          ) : isDiscount ? (
            /* Один в один із PriceRow у спецпропозиції (owner 2026-08-11:
               «переоформи, як там, де анімовано»): велика цифра стікає
               990 → 449, поруч перекреслена стара ціна і бейдж −55%. */
            <div className="flex items-center justify-center gap-3">
              <div
                className="font-display text-[2.5rem] font-bold leading-none text-primary tabular-nums"
                style={{ letterSpacing: "-0.02em" }}
              >
                {priceShown} ₴
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="font-mono text-[0.8125rem] text-ink-dim line-through tabular-nums leading-none">
                  {priceFrom} ₴
                </span>
                <span className="rounded-full bg-primary px-2 py-0.5 font-display text-[0.625rem] font-bold uppercase leading-none text-white">
                  −{discount}%
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="text-center flex-1">
                {/* Owner 2026-08-11: «від» обов'язково — подарунок діє і на
                    3/6/12 місяців, а не лише на місячний тариф. */}
                <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
                  Оплачуєш від
                </div>
                {/* Owner 2026-08-11: обидві половини — ОДНАКОВИМ кеглем і тим
                    самим словом «днів». Раніше зліва 20px «30 днів», справа
                    26px «14 дн.» — виглядало кривувато. Акцент тримає колір. */}
                <div className="font-display text-[1.4375rem] font-bold text-ink leading-none tabular-nums">
                  30 днів
                </div>
              </div>
              <span className="text-primary shrink-0 font-display text-[1.125rem] font-bold">
                +
              </span>
              <div className="text-center flex-1">
                <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
                  У подарунок
                </div>
                <div className="font-display text-[1.4375rem] font-bold text-primary leading-none tabular-nums">
                  {bonusShown} днів
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Owner 2026-08-11 (передумав): блок лишається всім — просто для
            подарунка за відгук строк 30 днів, а не 72 години. */}
        <div className="rounded-2xl bg-bg/50 px-3.5 py-2.5 text-[0.78125rem] mb-3 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-ink-dim">Умова</span>
            <span className="font-semibold text-ink">
              {isTrial
                ? "без умов"
                : isDiscount
                  ? "місяць підписки"
                  : "тариф від місяця"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-ink-dim">Лишилось часу</span>
            <span className="font-semibold text-ink tabular-nums">{left}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={invoiceMut.isPending ? undefined : handleClick}
          disabled={invoiceMut.isPending}
          className="w-full rounded-2xl bg-primary text-white py-3 font-display text-[0.875rem] font-semibold active:bg-primary/90 transition disabled:opacity-60"
        >
          {invoiceMut.isPending ? "Готуємо рахунок…" : gift.button || "Обрати тариф"}
        </button>
        <div className="mt-2 text-center text-[0.6875rem] text-ink-faint [text-wrap:balance]">
          {isTrial
            ? ""
            : hasOwnPrice
              ? "Ціна діє лише за цією кнопкою і лише для тебе."
              : "Подарунок нарахується автоматично після оплати — вводити нічого не треба."}
        </div>
      </div>
    </div>,
    document.body,
  );
}
